import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Star, Users, BarChart3, Shield } from 'lucide-react';

const Hero: React.FC = () => {
  const valuePropositions = [
    {
      icon: Users,
      title: 'Multi-role support',
      description: 'Students, teachers, parents, and administrators'
    },
    {
      icon: BarChart3,
      title: 'Real-time analytics',
      description: 'Progress tracking and insights'
    },
    {
      icon: Shield,
      title: 'Secure platform',
      description: 'User-friendly and scalable'
    }
  ];

  return (
    <section className="relative bg-gradient-to-br from-primary-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium">
                <Star size={16} className="mr-2" />
                Trusted by 500+ educational institutions worldwide
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 leading-tight">
                Transform Education with
                <span className="text-primary-600 block">Modern Learning Management</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl">
                Empower educators, engage students, and streamline learning with our comprehensive LMS platform.
              </p>
            </div>

            {/* Value Propositions */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {valuePropositions.map((proposition, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <proposition.icon size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{proposition.title}</p>
                    <p className="text-xs text-gray-600">{proposition.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Start Free Trial
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <a
                href="https://lernix.ciphermind.tech"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 border border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-lg transition-colors duration-200"
              >
                <Play size={20} className="mr-2" />
                Live Demo
              </a>
            </div>

            {/* Social Proof */}
            <div className="flex items-center space-x-6 pt-8">
              <div className="flex -space-x-2">
                {[
                  '/images/institute/institute2.png',
                  '/images/institute/institute1.png',
                  '/images/institute/institute3.png',
                  '/images/institute/institute4.jpg',
                  '/images/institute/institute5.jpg'
                ].map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Institution ${index + 1}`}
                    className="w-10 h-10 rounded-full border-2 border-white object-cover"
                  />
                ))}
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">500+</span> institutions trust Lernix
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="relative z-10">
              <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <div className="text-sm text-gray-500">Lernix Dashboard</div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="h-20 bg-primary-100 rounded-lg flex items-center justify-center">
                      <span className="text-primary-600 font-semibold">Courses</span>
                    </div>
                    <div className="h-20 bg-green-100 rounded-lg flex items-center justify-center">
                      <span className="text-green-600 font-semibold">Students</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute top-10 -right-10 w-20 h-20 bg-yellow-400 rounded-full opacity-20 animate-pulse"></div>
            <div className="absolute bottom-10 -left-10 w-16 h-16 bg-primary-400 rounded-full opacity-20 animate-pulse delay-1000"></div>
          </div>
        </div>
      </div>

      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100 rounded-full opacity-10 -translate-y-48 translate-x-48"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-100 rounded-full opacity-10 translate-y-32 -translate-x-32"></div>
    </section>
  );
};

export default Hero; 